import { brandOriginUrl } from '@/api';
import {
  formatProviderLimits,
  type PaymentProvider,
} from '@/domain/payments';
import {
  isRedepositProvider,
  paymentProviderLogoUrl,
} from '@/domain/payment-providers';
import { cn } from '@/lib/cn';
import { Badge, Icon } from '@/ui/common';

export interface ProviderRowProps {
  provider: PaymentProvider;
  currency?: string;
  selected?: boolean;
  onClick?: () => void;
  redepositLabel?: string;
  className?: string;
}

export function ProviderRow({
  provider,
  currency = '',
  selected = false,
  onClick,
  redepositLabel = 'Redeposit',
  className,
}: ProviderRowProps) {
  const logoSrc = paymentProviderLogoUrl(brandOriginUrl(), provider.Name, provider.Logo);
  const redeposit = isRedepositProvider(provider);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-border-subtle bg-bg-surface px-4 py-3 text-left transition-colors',
        'hover:border-border-focus hover:bg-bg-elevated',
        selected && 'border-action-primary ring-1 ring-action-primary',
        className,
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-bg-elevated p-1.5">
        <img
          src={logoSrc}
          alt=""
          className="h-full w-full object-contain"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.style.visibility = 'hidden';
          }}
        />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[14px] font-bold text-text-primary">{provider.Name}</span>
          {redeposit ? (
            <Badge tone="info" size="sm">
              {redepositLabel}
            </Badge>
          ) : null}
        </span>
        <span className="truncate text-[12px] text-text-secondary">
          {formatProviderLimits(provider, currency)}
        </span>
      </span>
      <Icon name="arrow-right" size={16} className="shrink-0 text-text-secondary" />
    </button>
  );
}
